// /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/header/SubmenuDesktop.tsx
import React, { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../lang/index.lang.tsx";
import Button from "../ui/Button.tsx";
import type { DropdownType } from "./navigationConfig.tsx";

interface SubmenuDesktopProps {
  type: DropdownType;
  isOpen: boolean;  // ← NEU
  onClose: () => void;
}

interface SubmenuDesktopRef {
  forceClosePanel: () => Promise<void>;
}

interface DropdownItem {
  name: string;
  href: string;
}

interface SpotlightLink {
  name: string;
  href: string;
}

interface DropdownConfig {
  titleKey: string;
  descriptionKey: string;
  buttonHref: string;
  buttonTextKey: string;
  items: DropdownItem[];
  spotlightTitleKey: string;
  spotlightLinks: SpotlightLink[];
}

const GRID_STYLE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "20% 5% 45% 5% 25%",
  gap: 0,
};

const SubmenuDesktop = React.forwardRef<SubmenuDesktopRef, SubmenuDesktopProps>(
  ({ type, isOpen, onClose }, ref) => { 
    const { t } = useLanguage();
    const navigate = useNavigate();

    const dropdownConfigs = useMemo(
      (): Record<DropdownType, DropdownConfig> => ({
        services: {
          titleKey: "services.heading.subtitle",
          descriptionKey: "services.heading.description",
          buttonHref: "/services",
          buttonTextKey: "services.heading.viewAll",
          items: [
            {name: t("services.design-build.title"), href: "/services/design-build"},
            {name: t("services.general-planning.title"), href: "/services/general-planning"},
            {name: t("services.construction-services.title"), href: "/services/construction-services"},
            {name: t("services.due-diligence.title"), href: "/services/due-diligence"},
            {name: t("services.energy-advisory.title"), href: "/services/energy-advisory"},
            {name: t("services.development-strategies.title"), href: "/services/development-strategies"},
            {name: t("services.fire-protection.title"), href: "/services/fire-protection"},
          ],
          spotlightTitleKey: "dropdown.services.spotlight",
          spotlightLinks: [
            {name: t("dropdown.expertise.branchSolutions.healthcare"), href: "/industries/health-care"},
            {name: t("dropdown.expertise.branchSolutions.offices"), href: "/industries/offices"},
            {name: t("dropdown.expertise.branchSolutions.residential"), href: "/industries/residential"},
            {name: t("dropdown.expertise.branchSolutions.retail"), href: "/industries/retail"},
          ],
        },
        industries: {
          titleKey: "industries.heading.subtitle",
          descriptionKey: "industries.heading.description",
          buttonHref: "/industries",
          buttonTextKey: "industries.heading.viewAll",
          items: [
            {name: t("industries.commercial-retail.title"), href: "/industries/commercial-retail"},
            {name: t("industries.food-reverage.title"), href: "/industries/food-reverage"},
            {name: t("industries.health-care.title"), href: "/industries/health-care"},
            {name: t("industries.offices-coworking.title"), href: "/industries/offices-coworking"},
            {name: t("industries.residential.title"), href: "/industries/residential"},
            {name: t("industries.logistics-industrial.title"), href: "/industries/logistics-industrial"},
            {name: t("industries.hospitality.title"), href: "/industries/hospitality"},
                    
          ],
          spotlightTitleKey: "dropdown.industries.spotlight",
          spotlightLinks: [
            {name: t("dropdown.services.general-planning"), href: "/services/general-planning"},
            {name: t("dropdown.services.design-build"), href: "/services/design-build"},
            {name: t("dropdown.services.construction-services"), href: "/services/construction"},
            {name: t("dropdown.services.esg"), href: "/services/esg"},
          ],
        },
        about: {
          titleKey: "dropdown.about.title",
          descriptionKey: "dropdown.about.description",
          buttonHref: "/about",
          buttonTextKey: "dropdown.about.viewAll",
          items: [
            {name: t("dropdown.about.aboutItems.company"), href: "/about/company"},
            {name: t("dropdown.about.aboutItems.people"), href: "/about/people"},
            {name: t("dropdown.about.aboutItems.leadership"), href: "/about/leadership"},
            {name: t("dropdown.about.aboutItems.inclusion"), href: "/about/inclusion"},
            {name: t("dropdown.about.aboutItems.sustainability"), href: "/about/sustainability"},
            {name: t("dropdown.about.businessItems.designBuild"), href: "/business/design-build"},
            {name: t("dropdown.about.businessItems.projectDevelopment"), href: "/business/project-development"},
            {name: t("dropdown.about.businessItems.facilityManagement"), href: "/business/facility-management"},
          ],
          spotlightTitleKey: "dropdown.about.spotlight",
          spotlightLinks: [
            {name: t("dropdown.about.contactItems.location"), href: "/contact/location"},
            {name: t("dropdown.about.contactItems.contact"), href: "/contact/contact"},
            {name: t("dropdown.about.aboutItems.sustainability"), href: "/about/sustainability"},
            {name: t("dropdown.about.aboutItems.inclusion"), href: "/about/inclusion"},
          ],
        },
        career: {
          titleKey: "dropdown.career.title",
          descriptionKey: "dropdown.career.description",
          buttonHref: "/careers",
          buttonTextKey: "dropdown.career.viewAll",
          items: [
            {name: t("dropdown.career.careerStart.findJobs"), href: "/careers/jobs"},
            {name: t("dropdown.career.careerStart.students"), href: "/career/students"},
            {name: t("dropdown.career.careerStart.university"), href: "/career/university"},
            {name: t("dropdown.career.careerStart.graduates"), href: "/career/graduates"},
            {name: t("dropdown.career.careerStart.experienced"), href: "/career/experienced"},
            {name: t("dropdown.career.careerPath.planning"), href: "/career/planning-team"},
            {name: t("dropdown.career.careerPath.dueDiligence"), href: "/career/due-diligence-team"},
            {name: t("dropdown.career.careerPath.construction"), href: "/career/construction-team"},
          ],
          spotlightTitleKey: "dropdown.career.spotlight",
          spotlightLinks: [
            {name: t("dropdown.career.expectations.responsibility"), href: "/career/responsibility"},
            {name: t("dropdown.career.expectations.inclusion"), href: "/career/inclusion"},
            {name: t("dropdown.career.expectations.benefits"), href: "/career/benefits"},
            {name: t("dropdown.career.careerStart.graduates"), href: "/career/graduates"},
          ],
        },
      }),
      [t]
    );

    const config = dropdownConfigs[type];

    React.useImperativeHandle(
      ref,
      () => ({
        forceClosePanel: () => Promise.resolve(),
      }),
      []
    );

    const handleItemClick = (href: string) => {
      onClose();
      setTimeout(() => navigate(href), 300);
    };

    return (
      <div className="w-full bg-[#212121]">
        <div className="max-w-[1510px] mx-auto px-4 sm:px-6 lg:px-8">
          <div style={GRID_STYLE}>
            {/* Linke Spalte: Titel, Beschreibung, Button */}
            <div className="pt-[50px] pb-[30px]">
              <h3 className="text-[25px] font-semibold text-white mb-5">
                {t(config.titleKey)}
              </h3>
              <p className="text-[15px] leading-relaxed text-white/90 mb-5">
                {t(config.descriptionKey)}
              </p>
              <Button
                variant="bordered"
                size="M"
                href={config.buttonHref}
                onClick={(e: any) => {
                  if (e && e.preventDefault) e.preventDefault();
                  handleItemClick(config.buttonHref);
                }}
              >
                {t(config.buttonTextKey)}
              </Button>
            </div>

            {/* Gap */}
            <div className="pt-[50px] pb-[30px]" />

            {/* Mittlere Spalte: Item-Links */}
            <div className="pt-[50px] pb-[30px]">
              <div className="border-t border-white/25" />
              <div className="flex flex-col">
                {config.items.map((item, index) => (
                  <React.Fragment key={item.href}>
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(item.href);
                      }}
                      className="nav-stripe-thick relative flex items-center justify-between w-full py-2 px-5 text-[15px] font-medium text-white transition-all duration-200 hover:bg-[#333333] focus:bg-[#333333] outline-none text-left min-h-[50px] bg-[#212121] no-underline"
                    >
                      <span className="flex-1">{item.name}</span>
                      <span className="flex-shrink-0">
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    </a>
                    {index < config.items.length - 1 && (
                      <div className="border-t border-white/25" />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="border-b border-white/25" />

              {/* Close Button */}
              <div className="flex justify-center mt-[25px]">
                <button
                  onClick={onClose}
                  className="inline-flex items-center text-white/60 hover:text-white text-xs font-medium transition-all duration-200 px-3 py-1 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5"
                >
                  <span>{t("common.close")}</span>
                  <svg className="ml-1 w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 25 25">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Gap */}
            <div className="pt-[50px] pb-[30px]" />

            {/* Rechte Spalte: Spotlight Links */}
            <div className="pt-[50px] pb-[30px]">
              <h4 className="text-[25px] font-semibold text-white mb-[17px]">
                {t(config.spotlightTitleKey)}
              </h4>
              <ul className="space-y-[12px] text-[15px]">
                {config.spotlightLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-white hover:text-blue-400 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(link.href);
                      }}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-b border-white/30" />
      </div>
    );
  }
);

SubmenuDesktop.displayName = "SubmenuDesktop";

export default SubmenuDesktop;
